import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, lastValueFrom } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';

import { ClientFilterObject } from '@common/filter.decorator';

import { C } from 'src/app/@shared/constants';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

import { Protocol } from '@api/protocol/@shared/protocol.entity';
import { ProtocolModel } from 'src/app/protocol/@shared/protocol.model';

@Injectable({
  providedIn: 'root',
})
export class ProtocolService extends ContentService<ProtocolModel, Protocol> {
  private path = 'protocols';

  constructor(private http: HttpClient) {
    super();
  }

  public loadContent(id: string, options: ContentOptions<Protocol> = {}): Observable<ProtocolModel[] | null> {
    if (!options.type) {
      console.warn('[ProtocolService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    return this.loadDetailContent(id, options).pipe(map((item: ProtocolModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public create(attributes: Partial<Protocol>, storeId: string): Promise<ProtocolModel> {
    return lastValueFrom(this.http.post<Protocol>(ProtoUrl.getUrl(this.path), attributes)
      .pipe(map((item: Protocol) => { return new ProtocolModel(item); }),
        tap((item: ProtocolModel) => {
          if (this.metadataStore[storeId]) {
            this.metadataStore[storeId].totalCount += 1;
          }

          this.updateItem(item, storeId, true);
        })));
  }

  public patch(id: string, attributes: Partial<Protocol>): Promise<ProtocolModel> {
    return this.http.patch<Protocol>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: Protocol) => { return new ProtocolModel(item); }),
        tap((item: ProtocolModel) => {
          this.updateItem(item);
        }))
      .toPromise();
  }

  public delete(id: string): Promise<boolean> {
    return this.http.delete<Protocol>(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(map(() => {
        return true;
      }),
      tap(() => {
        this.removeItem(id);
      }))
      .toPromise();
  }

  protected loadListContent(id: string, options: ContentOptions<Protocol>): Observable<ProtocolModel[]> {
    const filter: ClientFilterObject<Protocol> = this.getNormalizedFilter(id, options.filter);
    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[ProtocolService] load items', url);
    let totalCount: number;

    return this.http.get<Protocol[]>(url, {
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<Protocol[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<Protocol[]>) => { return response.body || []; }),
        map((items: Protocol[]) => { return items.map((item) => { return new ProtocolModel(item); }); }),
        tap((items: ProtocolModel[]) => {
          return this.setItems(items, id, filter, totalCount);
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<Protocol>): Observable<ProtocolModel> {
    const { filter } = options;
    const url = ProtoUrl.getUrl(`${this.path}/${id}`, {
      filter,
    });

    return this.http.get<Protocol>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item) => {
          return item
            ? new ProtocolModel(item)
            : null;
        }),
        tap((item) => {
          this.updateItem(item);
        }));
  }
}
